<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <h4 class="font-weight-bold">{{ headline }}</h4>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="bc-wrap">
          <div class="card">
            <div class="card-body">
              <div v-if="loading"
                   class="bc-load"><i class="fal fa-spinner-third fa-spin"></i></div>
              <BcReport v-else-if="reportId"
                        :id="reportId"
                        :pages="pages"
                        @back="goHome" />
              <BcTemplates v-else-if="view === 'templates'"
                           @back="goHome"
                           @use="useTemplate" />
              <div v-else-if="gateFailed"
                   class="bc-fail">
                <p class="bc-error">{{ error || $t("messenger.unavailable") }}</p>
                <button type="button"
                        class="btn btn-primary btn-lg"
                        @click="retry">{{ $t("messenger.retry") }}</button>
              </div>
              <template v-else>
                <p v-if="error"
                   class="bc-error">{{ error }}</p>
                <BcHome :gated="gated"
                        :rows="rows"
                        @create="openWizard(null)"
                        @saved="goTemplates"
                        @customers="goCustomers"
                        @connect="goConnect"
                        @open="openReport" />
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <BcWizard v-if="wizard"
              :pages="pages"
              :resume="resume"
              @close="wizard = false"
              @sent="onSent"
              @reconnect="goConnect" />
  </div>
</template>

<script>
import BcHome from "@/components/derived/broadcast/BcHome";
import BcReport from "@/components/derived/broadcast/BcReport";
import BcTemplates from "@/components/derived/broadcast/BcTemplates";
import BcWizard from "@/components/derived/broadcast/BcWizard";

const POLL_MS = 5000;

// บรอดแคสต์ home. The report and the saved-messages library are views of this same route
// (?r=<id>, ?v=templates), so the sidebar item stays on. The 5-step popup opens over the home.
export default {
  name: "Seller-Broadcast",
  components: { BcHome, BcReport, BcTemplates, BcWizard },

  data() {
    return {
      gate: null, // GET /api/broadcast/gate .state
      pages: [],
      rows: [],
      loading: true,
      error: "",
      gateFailed: false, // the gate answer did not come: only the error and a retry, never a live สร้างบรอดแคสต์
      wizard: false,
      resume: null,
      timer: null,
    };
  },

  computed: {
    reportId() {
      return this.$route.query.r || "";
    },
    view() {
      return this.$route.query.v || "";
    },
    gated() {
      return this.gate === "connect" || this.gate === "reconnect";
    },
    headline() {
      if (this.reportId) return this.$t("broadcast.screens.report.title");
      if (this.view === "templates") return this.$t("broadcast.screens.library.title");
      return this.$t("broadcast.screens.home.title");
    },
  },

  created() {
    this.load();
    // the home cards follow a broadcast that is still sending
    this.timer = setInterval(() => {
      if (!this.reportId && !this.view && this.rows.some((r) => r.status === "sending")) this.loadRows();
    }, POLL_MS);
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  methods: {
    ask(action, payload) {
      return new Promise((resolve) => {
        this.$store.dispatch("broadcast/" + action, Object.assign({}, payload, { callback: (ok, data) => resolve({ ok, data }) }));
      });
    },

    async load() {
      this.gateFailed = false;
      this.error = "";
      const gate = await this.ask("fetchGate");
      this.gate = gate.ok && ["ready", "connect", "reconnect"].includes(gate.data.state) ? gate.data.state : "ready";
      if (this.gate === "ready") {
        const [pages] = await Promise.all([this.ask("fetchPages"), this.loadRows()]);
        if (pages.ok) this.pages = pages.data.pages || [];
      }
      this.loading = false;
    },

    retry() {
      this.loading = true;
      this.load();
    },

    async loadRows() {
      const { ok, data } = await this.ask("fetchBroadcasts", {});
      if (ok) this.rows = data.rows || [];
      else if (!this.rows.length) this.error = data.messageTh || "";
    },

    go(query) {
      return new Promise((resolve) => {
        this.$router.push({ name: "Seller-Broadcast", query }, () => resolve(true), () => resolve(false));
      });
    },

    goHome() {
      this.go({}).then(() => this.loadRows());
    },

    goTemplates() {
      this.go({ v: "templates" });
    },

    goCustomers() {
      this.$router.push({ name: "Seller-Broadcast-Customers" });
    },

    // The Facebook connect flow lives in ตั้งค่า › การจัดส่ง
    goConnect() {
      this.$router.replace({ name: "Seller-Settings-Shipping" }, () => {
        this.wizard = false;
      });
    },

    openReport(id) {
      this.go({ r: String(id) });
    },

    openWizard(resume) {
      this.resume = resume;
      this.wizard = true;
    },

    // ใช้ข้อความนี้: back to the home, popup at step 4
    useTemplate(choice) {
      this.go({}).then(() => this.openWizard(choice));
    },

    // The route moves first: the popup then gives its history entry back only if the route is still the home
    onSent(id) {
      this.$router.replace({ name: "Seller-Broadcast", query: { r: String(id) } }, () => {
        this.wizard = false;
      }, () => {
        this.wizard = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.bc-wrap {
  max-width: 760px;
  margin: 0 auto;
}

.bc-load,
.bc-error {
  text-align: center;
  color: #666;
  padding: 2rem 0;
}

.bc-fail {
  text-align: center;

  .btn {
    min-height: 44px;
  }
}

body.--dark {
  .bc-load,
  .bc-error {
    color: #c4c4c4;
  }
}
</style>

